import React, { useEffect } from "react";
import PrivateLayout from "layouts/privateLayout";
import { Box, Tab, Tabs } from "@mui/material";
import BasicBreadcrumbs from "components/breadcrumb";
import ImportButton from "./importButton2";
import ExportButton from "./exportButton";
import ListingTable from "./listing";
import axios from "axios";
import Header from "components/header";
import CustomTabPanel from "components/customTabPanel";
import { useApiClient } from "context/ApiClientContext";
import ErrorAlert from "helper/errorAlert";

export const DataContext = React.createContext();
export const CompaniesContext = React.createContext();

function Payable() {
  const status = [
    { name: "" },
    { name: "error" },
    { name: "rejected" },
    { name: "dispute" },
    { name: "open" },
    { name: "void" },
  ];

  const [companyList, setCompanyList] = React.useState([]);
  const [value, setValue] = React.useState(0);
  const [filteredDataList, setFilteredDataList] = React.useState([]);
  const [currentPage, setCurrentPage] = React.useState(0);
  const [totalPage, setTotalPage] = React.useState(0);
  const [totalRecordCount, setTotalRecordCount] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(100);
  const [sortCriteria, setSortCriteria] = React.useState("id desc");
  const [extraFilters, setExtraFilters] = React.useState("");
  const companyId = localStorage.getItem('companyId')
  const apiClient = useApiClient();

  const fetchCompanyDetails = async () => {
    try {
      const response = await axios
        .get(`${apiClient}/company`, {
          headers: {
              Authorization:`Bearer ${localStorage.getItem('token')}`
          }
        })
        .then((response) => {
          return response.data;
        });
  
      setCompanyList(response);
    } catch (error) {
      ErrorAlert(error);
    }
  };

  const fetchDataAndUpdateList = async (tabIndex, page, size, sort, activeFilters) => {
    try {
      let filter = "";
      const encodedSource = encodeURIComponent(`docSource='AP'`);

      switch (tabIndex) {
        case 0:
          filter = encodeURIComponent(` and status != 'void'`);
          break;
        case 1:
        case 2:
        case 3:
        case 4:
          if (tabIndex < status.length) {
            const statusName = status[tabIndex].name;
            filter = encodeURIComponent(` and status='${statusName}'`);
          }
          break;
        default:
          filter = ``;
          break;
      }

      if (activeFilters) {
        filter += encodeURIComponent(` and (${activeFilters})`);
      }

      const response = await axios.get(
        `${apiClient}/${companyId}/document/header_v2?sortcriteria=${encodeURIComponent(sort)}&filters=${encodedSource}${filter}`,
        {
          headers: {
            Authorization:`Bearer ${localStorage.getItem('token')}`,
            Accept: "application/json",
          },
          params: {
            pagenum: page + 1,
            pagesize: size,
          },
        }
      );

      const { docHead, totalPage, totalRecord } = response.data;
      setTotalPage(totalPage);
      setTotalRecordCount(totalRecord);
      setCurrentPage(page);
      
      const dataWithKeys = docHead ? docHead.map((item) => ({
        ...item,
        remark: item.remark ? item.remark.replace(/\r\n|\r|\n/g, "\n") : "",
      })) : [];
      setFilteredDataList(dataWithKeys);
    } catch (error) {
      if (error?.response?.status === 404) {
        setFilteredDataList([]);
        setTotalRecordCount(0);
        setTotalPage(0);
      } else {
        console.error("An error occurred:", error?.response?.statusText);
      }
    }
  };

  useEffect(() => {
    fetchCompanyDetails();
  }, []);

  useEffect(() => {
    fetchDataAndUpdateList(value, currentPage, pageSize, sortCriteria, extraFilters);
  }, [value, currentPage, pageSize, sortCriteria, extraFilters]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
    setCurrentPage(0);
    setExtraFilters(""); // Clear filters when changing tab
  };

  const handleGridStateChange = (newPage, newPageSize, newSort, newFilters) => {
    if (newPage !== currentPage) setCurrentPage(newPage);
    if (newPageSize !== pageSize) setPageSize(newPageSize);
    if (newSort !== sortCriteria) setSortCriteria(newSort);
    if (newFilters !== extraFilters) setExtraFilters(newFilters);
  };

  return (
    <PrivateLayout>
      <Box sx={{ width: "100%" }}>
        <BasicBreadcrumbs first="Payable" second="Listing" />

        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Header title="Account Payable Transactions" />
          <Box sx={{ display: "flex" }}>
            <ImportButton />
            <ExportButton />
          </Box>
        </Box>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={value}
            onChange={handleChange}
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab label="All" />
            <Tab label="Error" />
            <Tab label="Rejected " />
            <Tab label="Dispute " />
            <Tab label="Open " />
          </Tabs>
        </Box>
        <DataContext.Provider value={filteredDataList}>
          <CompaniesContext.Provider value={companyList}>
            <CustomTabPanel value={value} index={0}>
              <ListingTable
                isCheckbox={false}
                isSoftDelete={false}
                currentPage={currentPage}
                totalPage={totalPage}
                totalRecord={totalRecordCount}
                pageSize={pageSize}
                sortCriteria={sortCriteria}
                extraFilters={extraFilters}
                tabIndex={value}
                onGridStateChange={handleGridStateChange}
              />
            </CustomTabPanel>
            <CustomTabPanel value={value} index={1}>
              <ListingTable
                isCheckbox={false}
                isSoftDelete={true}
                currentPage={currentPage}
                totalPage={totalPage}
                totalRecord={totalRecordCount}
                pageSize={pageSize}
                sortCriteria={sortCriteria}
                extraFilters={extraFilters}
                tabIndex={value}
                onGridStateChange={handleGridStateChange}
              />
            </CustomTabPanel>
            <CustomTabPanel value={value} index={2}>
              <ListingTable
                isCheckbox={false}
                isSoftDelete={true}
                currentPage={currentPage}
                totalPage={totalPage}
                totalRecord={totalRecordCount}
                pageSize={pageSize}
                sortCriteria={sortCriteria}
                extraFilters={extraFilters}
                tabIndex={value}
                onGridStateChange={handleGridStateChange}
              />
            </CustomTabPanel>
            <CustomTabPanel value={value} index={3}>
              <ListingTable
                isCheckbox={false}
                isSoftDelete={false}
                currentPage={currentPage}
                totalPage={totalPage}
                totalRecord={totalRecordCount}
                pageSize={pageSize}
                sortCriteria={sortCriteria}
                extraFilters={extraFilters}
                tabIndex={value}
                onGridStateChange={handleGridStateChange}
              />
            </CustomTabPanel>
            <CustomTabPanel value={value} index={4}>
              <ListingTable
                isCheckbox={true}
                isSoftDelete={false}
                currentPage={currentPage}
                totalPage={totalPage}
                totalRecord={totalRecordCount}
                pageSize={pageSize}
                sortCriteria={sortCriteria}
                extraFilters={extraFilters}
                tabIndex={value}
                onGridStateChange={handleGridStateChange}
              />
            </CustomTabPanel>
          </CompaniesContext.Provider>
        </DataContext.Provider>
      </Box>
    </PrivateLayout>
  );
}

export default Payable;
